import { useState } from "react";
import { Link } from "react-router-dom";
import { Page } from "../components/AppShell";
import {
  Badge,
  Banner,
  Card,
  Pager,
  PanelState,
  RangeFilter,
  StatTile,
} from "../components/ui";
import { useAuth } from "../auth/AuthContext";
import { BlockForm, useBlock, type BlockRequest } from "../lib/useBlock";
import { date, dateTime, device, num, relative } from "../lib/format";
import { useApi } from "../lib/useApi";
import type {
  BlockState,
  BlockedResponse,
  Offender,
  SuspiciousEventsResponse,
  SuspiciousResponse,
} from "../lib/types";

/**
 * ── Everyone the API has had to refuse ─────────────────────────────────────
 * Every rate limiter in the API records the requests it turns away (see its
 * src/lib/suspicious.ts). This is that record, read as a shortlist, plus the
 * one lever that does something lasting about it.
 *
 * ── It is a shortlist, not a verdict, and the page has to say so ───────────
 * A customer on a flaky train connection whose app retries will trip the
 * global net. So will a browser extension, and so will anyone behind
 * carrier-grade NAT sharing an address with a thousand other people. What
 * separates that from abuse is the SHAPE of it — which limiter, how many
 * times, over how long, from how many addresses — so every one of those is a
 * column rather than being folded into a score.
 *
 * Which is also why nothing here blocks automatically. The account most likely
 * to trip the expensive limiter is the account spending the most money on
 * renders, and a customer locked out by a rule nobody can point at is a
 * support call that cannot be answered. A person decides, and the reason they
 * type is kept.
 */
export function SuspiciousPage() {
  const [days, setDays] = useState(7);
  const [scope, setScope] = useState("");
  const [accountsOnly, setAccountsOnly] = useState(false);
  const [minHits, setMinHits] = useState(5);
  const [page, setPage] = useState(1);
  const [blockedPage, setBlockedPage] = useState(1);

  /** The offender whose raw buckets are expanded, if any. */
  const [drilled, setDrilled] = useState<string | null>(null);
  /** The offender whose block form is open, if any. */
  const [blocking, setBlocking] = useState<string | null>(null);

  const { canRevoke } = useAuth();

  const { data, error, loading, stale, reload } = useApi<SuspiciousResponse>(
    "/api/admin/suspicious",
    {
      days,
      scope: scope || undefined,
      accountsOnly: accountsOnly ? "1" : undefined,
      minHits,
      page,
      pageSize: 25,
    },
  );

  const blocked = useApi<BlockedResponse>("/api/admin/blocked", {
    page: blockedPage,
    pageSize: 50,
  });

  // One request per drill-down rather than every bucket up front: the feed is
  // the detail behind a row, and loading it for a page of 25 offenders would
  // be most of the table fetched to render nothing.
  const events = useApi<SuspiciousEventsResponse>(
    drilled ? "/api/admin/suspicious/events" : null,
    { days, identityKey: drilled ?? undefined, pageSize: 50 },
  );

  const refresh = () => {
    reload();
    blocked.reload();
    if (drilled) events.reload();
  };

  const { block, unblock, busyId, toast } = useBlock(refresh);
  const scopes = data?.scopes ?? {};

  return (
    <Page
      title="Suspicious activity"
      subtitle={
        data
          ? `${num(data.summary.hits)} requests refused from ${num(data.summary.offenders)} caller${data.summary.offenders === 1 ? "" : "s"}`
          : "Loading…"
      }
      actions={
        <RangeFilter
          days={days}
          onChange={(d) => {
            setDays(d);
            setPage(1);
          }}
        />
      }
    >
      {!canRevoke && (
        <Banner title="Read-only">
          Your role is Analyst, so you can see who is being refused but not
          block anyone. Ask an owner or admin if something here needs acting on.
        </Banner>
      )}

      <div className="grid grid-kpi">
        <StatTile
          label="Requests refused"
          value={data?.summary.hits ?? 0}
          hint="in this window"
        />
        <StatTile
          label="Callers refused"
          value={data?.summary.offenders ?? 0}
          hint={`${num(data?.summary.addresses ?? 0)} distinct addresses`}
        />
        <StatTile
          label="Signed-in accounts"
          value={data?.summary.accounts ?? 0}
          hint="the rest are unidentified addresses"
        />
        <StatTile
          label="Blocked now"
          value={data?.summary.blockedNow ?? 0}
          hint="not limited to this window"
        />
      </div>

      <Card
        title="Which limits are being hit"
        note="A limiter's name is the whole story. Tripping the lead export five times and tripping the global net five times are the same number and completely different events — the first is somebody pulling the customer list, the second is a scanner."
      >
        <PanelState
          loading={loading}
          error={error}
          empty={data?.summary.byScope.length === 0}
          emptyText="Nothing has been refused in this window."
        >
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Limit</th>
                  <th className="num">Requests refused</th>
                  <th className="num">Callers</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data?.summary.byScope.map((row) => (
                  <tr key={row.scope}>
                    <td className="cell-strong">
                      {scopes[row.scope] ?? row.scope}
                      <div className="cell-mute mono">{row.scope}</div>
                    </td>
                    <td className="num cell-strong">{num(row.hits)}</td>
                    <td className="num">{num(row.offenders)}</td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => {
                          setScope(scope === row.scope ? "" : row.scope);
                          setPage(1);
                        }}
                        aria-pressed={scope === row.scope}
                      >
                        {scope === row.scope ? "Clear filter" : "Filter"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PanelState>
      </Card>

      <Card
        title="Callers"
        note="Worst first. A row is somebody the API refused, not somebody who did something wrong — a retrying app on a bad connection lands here too. What to read is the limit, the count and the spread of addresses."
        aside={
          <div className="filters" style={{ margin: 0 }}>
            <select
              className="select"
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                setPage(1);
              }}
              aria-label="Filter by limit"
            >
              <option value="">All limits</option>
              {Object.entries(scopes).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="select"
              value={minHits}
              onChange={(e) => {
                setMinHits(Number(e.target.value));
                setPage(1);
              }}
              aria-label="Minimum refused requests"
            >
              <option value={1}>Everything</option>
              <option value={5}>5+ refusals</option>
              <option value={25}>25+ refusals</option>
              <option value={100}>100+ refusals</option>
            </select>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
              }}
            >
              <input
                type="checkbox"
                checked={accountsOnly}
                onChange={(e) => {
                  setAccountsOnly(e.target.checked);
                  setPage(1);
                }}
              />
              Signed-in accounts only
            </label>
          </div>
        }
      >
        <div className={stale ? "stale" : undefined}>
          <PanelState
            loading={loading}
            error={error}
            empty={data?.offenders.length === 0}
            emptyText="Nobody has been refused that often in this window."
          >
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Caller</th>
                    <th className="num">Refused</th>
                    <th>Limits tripped</th>
                    <th>Addresses</th>
                    <th>Last refused</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data?.offenders.map((row) => (
                    <OffenderRows
                      key={row.identityKey}
                      row={row}
                      scopes={scopes}
                      canAct={canRevoke}
                      // `!!row.userId &&` matters: most rows here have no
                      // account, and `null === null` would otherwise mark
                      // every one of them busy whenever nothing is in flight.
                      busy={!!row.userId && busyId === row.userId}
                      drilled={drilled === row.identityKey}
                      onDrill={() =>
                        setDrilled(
                          drilled === row.identityKey ? null : row.identityKey,
                        )
                      }
                      blocking={blocking === row.identityKey}
                      onToggleBlock={() =>
                        setBlocking(
                          blocking === row.identityKey ? null : row.identityKey,
                        )
                      }
                      onBlock={async (req) => {
                        const ok = await block(
                          row.userId!,
                          nameOf(row),
                          req,
                        );
                        if (ok) setBlocking(null);
                      }}
                      onUnblock={() => void unblock(row.userId!, nameOf(row), "from suspicious activity")}
                      events={
                        drilled === row.identityKey ? events : null
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              meta={data?.pagination}
              onChange={setPage}
              busy={stale}
              label="callers"
            />
          </PanelState>
        </div>
      </Card>

      <Card
        title="Currently blocked"
        note="Every account that cannot sign in right now, and why. Check this before telling someone on the phone that their account is fine. Temporary blocks disappear from here the moment they lapse."
      >
        <div className={blocked.stale ? "stale" : undefined}>
          <PanelState
            loading={blocked.loading}
            error={blocked.error}
            empty={blocked.data?.blocked.length === 0}
            emptyText="No accounts are blocked."
          >
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Kind</th>
                    <th>Reason</th>
                    <th>Blocked by</th>
                    <th>Since</th>
                    {canRevoke && <th />}
                  </tr>
                </thead>
                <tbody>
                  {blocked.data?.blocked.map((row) => (
                    <tr key={row.userId}>
                      <td>
                        <Link
                          to={`/customers/${row.userId}`}
                          className="cell-strong"
                          style={{ textDecoration: "underline" }}
                        >
                          {row.user?.fullName ?? "Unnamed"}
                        </Link>
                        <div className="cell-mute mono">
                          {row.user?.phone ?? row.userId}
                        </div>
                      </td>
                      <td>
                        <BlockBadge state={row} />
                      </td>
                      <td className="cell-mute">{row.reason ?? "—"}</td>
                      <td className="cell-mute">{row.byEmail ?? "—"}</td>
                      <td className="cell-mute">{dateTime(row.at)}</td>
                      {canRevoke && (
                        <td style={{ textAlign: "right" }}>
                          <button
                            className="btn btn-sm"
                            disabled={busyId === row.userId}
                            onClick={() =>
                              void unblock(
                                row.userId,
                                row.user?.fullName ?? "this customer",
                                "lifted from the blocked list",
                              )
                            }
                          >
                            {busyId === row.userId ? "Lifting…" : "Unblock"}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              meta={blocked.data?.pagination}
              onChange={setBlockedPage}
              busy={blocked.stale}
              label="blocked accounts"
            />
          </PanelState>
        </div>
      </Card>

      <p className="card-note">
        Addresses are only as good as <span className="mono">TRUST_PROXY</span>{" "}
        on the API. Behind a load balancer with it unset, every row records the
        balancer's address — which makes the whole API look like one very busy
        caller and this page much less useful than it should be.
      </p>
      {toast}
    </Page>
  );
}

/** `Priya Sharma` or, for an anonymous address, the address itself. */
function nameOf(row: Offender): string {
  return row.user?.fullName ?? row.user?.phone ?? row.identityKey;
}

function BlockBadge({ state }: { state: BlockState }) {
  if (!state.blocked) return <Badge tone="good">Active</Badge>;
  if (state.permanent) return <Badge tone="critical">Blocked</Badge>;
  return (
    <Badge tone="warning">
      Until {state.until ? dateTime(state.until) : "—"}
    </Badge>
  );
}

/**
 * One offender, plus the two rows that can expand under it.
 *
 * A fragment of three `<tr>` rather than a nested table: the block form and the
 * bucket feed belong to the row above them, and a table inside a cell loses
 * the column alignment that makes the parent readable.
 */
function OffenderRows({
  row,
  scopes,
  canAct,
  busy,
  drilled,
  onDrill,
  blocking,
  onToggleBlock,
  onBlock,
  onUnblock,
  events,
}: {
  row: Offender;
  scopes: Record<string, string>;
  canAct: boolean;
  busy: boolean;
  drilled: boolean;
  onDrill: () => void;
  blocking: boolean;
  onToggleBlock: () => void;
  onBlock: (req: BlockRequest) => void;
  onUnblock: () => void;
  events: ReturnType<typeof useApi<SuspiciousEventsResponse>> | null;
}) {
  // Anonymous callers are the majority of this table and there is nothing to
  // block: a block lives on a User row. The honest thing is to say so rather
  // than offer a button that cannot work — the answer for an address is a
  // firewall rule, which is not this console's job.
  const actionable = canAct && !!row.userId;
  const isBlocked = row.block?.blocked ?? false;

  return (
    <>
      <tr>
        <td>
          {row.userId ? (
            <Link
              to={`/customers/${row.userId}`}
              className="cell-strong"
              style={{ textDecoration: "underline" }}
            >
              {row.user?.fullName ?? "Unnamed account"}
            </Link>
          ) : row.adminId ? (
            <span className="cell-strong">Admin console</span>
          ) : (
            <span className="cell-strong">Not signed in</span>
          )}
          <div className="cell-mute mono">
            {row.user?.phone ?? row.identityKey}
          </div>
          <div className="cell-mute mono">{row.lastPath}</div>
        </td>
        <td className="num cell-strong">
          {num(row.hits)}
          <div className="cell-mute">
            {num(row.buckets)} window{row.buckets === 1 ? "" : "s"}
          </div>
        </td>
        <td>
          <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {row.scopes.map((s) => (
              <Badge key={s} tone={toneForScope(s)}>
                {scopes[s] ?? s}
              </Badge>
            ))}
          </span>
        </td>
        <td className="mono cell-mute">
          {row.addresses.length === 0 ? "—" : row.addresses.join(", ")}
          {row.lastUserAgent && (
            <div className="cell-mute">{device(row.lastUserAgent)}</div>
          )}
        </td>
        <td className="cell-mute">
          {relative(row.lastAt)}
          <div className="cell-mute">since {date(row.firstAt)}</div>
        </td>
        <td>{row.block ? <BlockBadge state={row.block} /> : <Badge>—</Badge>}</td>
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          <button className="btn btn-sm" onClick={onDrill}>
            {drilled ? "Hide" : "Detail"}
          </button>{" "}
          {actionable &&
            (isBlocked ? (
              <button
                className="btn btn-sm"
                disabled={busy}
                onClick={onUnblock}
              >
                {busy ? "Lifting…" : "Unblock"}
              </button>
            ) : (
              <button
                className="btn btn-sm btn-danger"
                disabled={busy}
                onClick={onToggleBlock}
              >
                {blocking ? "Cancel" : "Block"}
              </button>
            ))}
        </td>
      </tr>

      {blocking && actionable && (
        <tr>
          <td colSpan={7} style={{ background: "var(--surface-2)" }}>
            <BlockForm
              name={row.userId ?? row.identityKey}
              busy={busy}
              onSubmit={onBlock}
              onCancel={onToggleBlock}
            />
          </td>
        </tr>
      )}

      {drilled && (
        <tr>
          <td colSpan={7}>
            <PanelState
              loading={events?.loading ?? true}
              error={events?.error ?? null}
              empty={events?.data?.events.length === 0}
              emptyText="No buckets in this window."
            >
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Limit</th>
                      <th className="num">Refused</th>
                      <th>Allowed</th>
                      <th>Window</th>
                      <th>Last request</th>
                      <th>Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events?.data?.events.map((e) => (
                      <tr key={e.id}>
                        <td className="cell-strong">
                          {scopes[e.scope] ?? e.scope}
                        </td>
                        <td className="num cell-strong">{num(e.hits)}</td>
                        {/* What the limit was WHEN this was written. Limits get
                            tuned, and a bucket from March has to keep saying
                            what March allowed. */}
                        <td className="cell-mute">
                          {num(e.limit)} / {windowLabel(e.windowMs)}
                        </td>
                        <td className="cell-mute">
                          {dateTime(e.windowStart)}
                        </td>
                        <td className="mono cell-mute">
                          {e.method} {e.path}
                        </td>
                        <td className="mono cell-mute">{e.ip ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </PanelState>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * How alarming a limiter is to trip.
 *
 * The global net catches scanners all day and means almost nothing on its own.
 * The two that cost money — image generation — and the one that hands over
 * every lead's phone number are the rows worth a second look, so they are
 * coloured and the rest are not. A page where everything is red is a page
 * nobody reads.
 */
function toneForScope(
  scope: string,
): "neutral" | "good" | "warning" | "critical" {
  if (scope === "export" || scope === "admin.auth") return "critical";
  if (scope.startsWith("ai.")) return "warning";
  if (scope === "auth" || scope.startsWith("upload.")) return "warning";
  return "neutral";
}

/** `300000` → `5 min`. The limiter's window, as a person would say it. */
export function windowLabel(ms: number): string {
  if (ms >= 3_600_000) {
    const h = ms / 3_600_000;
    return `${h % 1 === 0 ? h : h.toFixed(1)} hr`;
  }
  if (ms >= 60_000) {
    const m = ms / 60_000;
    return `${m % 1 === 0 ? m : m.toFixed(1)} min`;
  }
  return `${Math.round(ms / 1000)} s`;
}
